﻿import * as THREE from 'three';
import type { GameSnapshot, RoomState } from '../../shared/types.js';
import { DEFAULT_RENDER_TUNING, type RenderTuning } from './renderTuning.js';
import { BOTTOM_HULL_CAMERA_POLICY } from './hostCameraPolicy.js';
import { WorldViewportView } from './WorldViewportView.js';
import type { GraphicsQuality } from './quality.js';
import { type BeamGlowState } from './hostWorldSceneLighting.js';
import { createCameraSpotlightVolumetricCone, updateCameraSpotlightVolumetricUniforms } from '../effects/volumetricLighting.js';

/**
 * Docking light parameters.
 *
 * Intensity: 75% of hullLightBrightness reference (16 × 0.75 = 12).
 * Angle / penumbra: matched to periscope spotlight at focus = 3.5.
 *   focusNarrowness = (3.5 − 0.2) / 3.8 ≈ 0.868
 *   angle    = lerp(π × 0.32, π × 0.08, 0.868) ≈ π × 0.112  (~20°)
 *   penumbra = lerp(0.92, 0.28, 0.868) ≈ 0.36
 * Decay = 2 → inverse-square distance falloff.
 */
const DOCKING_LIGHT_INTENSITY = 12.0;
const DOCKING_LIGHT_DISTANCE = 18;
const DOCKING_LIGHT_ANGLE = Math.PI * 0.112; // periscope focus 3.5 equivalent (~20°)
const DOCKING_LIGHT_PENUMBRA = 0.36;
// The two lamps sit fore/aft of the docking port and aim straight down, crossing here.
const DOCKING_LIGHT_CONVERGENCE_M = 10;

export class BottomHullCameraView {
  private readonly viewportView: WorldViewportView;
  private readonly portLight: THREE.SpotLight;
  private readonly starboardLight: THREE.SpotLight;
  private readonly portGlow: THREE.PointLight;
  private readonly starboardGlow: THREE.PointLight;
  private readonly lightTarget: THREE.Object3D;
  private engineOverloaded = false;
  private lastSnapshot: GameSnapshot | null = null;
  // Volumetric cones for port and starboard docking lights.
  private readonly portVolMesh: THREE.Mesh;
  private readonly portVolMat: THREE.ShaderMaterial;
  private readonly starboardVolMesh: THREE.Mesh;
  private readonly starboardVolMat: THREE.ShaderMaterial;
  // Glow states registered with the particle system.
  private readonly portGlowState: BeamGlowState = {
    source: new THREE.Vector3(), direction: new THREE.Vector3(0, 0, -1),
    angle: DOCKING_LIGHT_ANGLE, range: DOCKING_LIGHT_DISTANCE, intensity: 0
  };
  private readonly starboardGlowState: BeamGlowState = {
    source: new THREE.Vector3(), direction: new THREE.Vector3(0, 0, -1),
    angle: DOCKING_LIGHT_ANGLE, range: DOCKING_LIGHT_DISTANCE, intensity: 0
  };
  private readonly tempVec = new THREE.Vector3();

  constructor(container: HTMLElement, quality: GraphicsQuality, tuning: RenderTuning = DEFAULT_RENDER_TUNING, fpsOverride?: number) {
    this.viewportView = new WorldViewportView(container, quality, tuning, BOTTOM_HULL_CAMERA_POLICY, undefined, {
      framingAspectRatio: 3 / 4,
      resizeMode: 'cover',
      autoStartAnimation: true,
      terrainOnly: false,
      fpsOverride
    });

    const camera = this.viewportView.getCamera();

    this.lightTarget = new THREE.Object3D();
    this.lightTarget.position.set(0, 0, -DOCKING_LIGHT_CONVERGENCE_M);
    camera.add(this.lightTarget);

    this.portLight = new THREE.SpotLight(0x77d6ff, DOCKING_LIGHT_INTENSITY, DOCKING_LIGHT_DISTANCE, DOCKING_LIGHT_ANGLE, DOCKING_LIGHT_PENUMBRA, 2);
    this.portLight.position.set(0, 0.4, 0);
    this.portLight.target = this.lightTarget;
    this.portLight.castShadow = false;

    this.starboardLight = new THREE.SpotLight(0x77d6ff, DOCKING_LIGHT_INTENSITY, DOCKING_LIGHT_DISTANCE, DOCKING_LIGHT_ANGLE, DOCKING_LIGHT_PENUMBRA, 2);
    this.starboardLight.position.set(0, -0.4, 0);
    this.starboardLight.target = this.lightTarget;
    this.starboardLight.castShadow = false;

    this.portGlow = new THREE.PointLight(0x77d6ff, 0, 4, 2);
    this.portGlow.position.set(0, 0.4, 0);
    this.portGlow.castShadow = false;
    camera.add(this.portGlow);

    this.starboardGlow = new THREE.PointLight(0x77d6ff, 0, 4, 2);
    this.starboardGlow.position.set(0, -0.4, 0);
    this.starboardGlow.castShadow = false;
    camera.add(this.starboardGlow);

    camera.add(this.portLight);
    camera.add(this.starboardLight);

    // Volumetric cones: 3-D god-ray scatter for each docking light.
    const scene = this.viewportView.getScene();
    const color = new THREE.Color(0x77d6ff);
    const portVol = createCameraSpotlightVolumetricCone(color, DOCKING_LIGHT_ANGLE, DOCKING_LIGHT_DISTANCE, 100);
    this.portVolMesh = portVol.mesh;
    this.portVolMat = portVol.material;
    const sbVol = createCameraSpotlightVolumetricCone(color, DOCKING_LIGHT_ANGLE, DOCKING_LIGHT_DISTANCE, 101);
    this.starboardVolMesh = sbVol.mesh;
    this.starboardVolMat = sbVol.material;
    scene.add(this.portVolMesh, this.starboardVolMesh);

    // Register docking spotlights as particle glow sources.
    this.viewportView.setExternalParticleGlowSources([this.portGlowState, this.starboardGlowState]);
  }

  setRoomState(state: RoomState): void {
    this.viewportView.setRoomState(state);
  }

  setSnapshot(snapshot: GameSnapshot): void {
    this.viewportView.setSnapshot(snapshot);
    this.lastSnapshot = snapshot;
    this.engineOverloaded = snapshot.submarine.engineOverloaded;
    const enabled = snapshot.submarine.dockingLightsOn && !this.engineOverloaded;
    this.portLight.intensity = enabled ? DOCKING_LIGHT_INTENSITY : 0;
    this.starboardLight.intensity = enabled ? DOCKING_LIGHT_INTENSITY : 0;
    this.portGlow.intensity = enabled ? 0.9 : 0;
    this.starboardGlow.intensity = enabled ? 0.9 : 0;
  }

  advanceFrame(timestampMs: number): void {
    this.viewportView.advanceFrame(timestampMs);
    if (this.engineOverloaded) {
      const flicker = Math.max(0, Math.sin(timestampMs * 0.04) * 0.5 + 0.5);
      const intensity = flicker * DOCKING_LIGHT_INTENSITY * 0.72;
      const glowIntensity = flicker * 0.9 * 0.72;
      this.portLight.intensity = intensity;
      this.starboardLight.intensity = intensity;
      this.portGlow.intensity = glowIntensity;
      this.starboardGlow.intensity = glowIntensity;
    }

    // Update beam cones (cross-plane + volumetric) and particle glow states.
    const visibility = this.lastSnapshot?.world.visibility ?? 0.5;
    const elapsed = timestampMs / 1000;
    const camera = this.viewportView.getCamera();
    const scene = this.viewportView.getScene();
    const targetWorldPos = this.lightTarget.getWorldPosition(this.tempVec.clone());

    const entries = [
      { light: this.portLight, glowState: this.portGlowState, volMesh: this.portVolMesh, volMat: this.portVolMat },
      { light: this.starboardLight, glowState: this.starboardGlowState, volMesh: this.starboardVolMesh, volMat: this.starboardVolMat }
    ];

    for (const { light, glowState, volMesh, volMat } of entries) {
      const lightOn = light.intensity > 0;
      const lightWorldPos = light.getWorldPosition(this.tempVec.clone());

      // Volumetric cone.
      volMesh.visible = lightOn;
      if (lightOn) {
        volMesh.position.copy(lightWorldPos);
        volMesh.lookAt(targetWorldPos);
        updateCameraSpotlightVolumetricUniforms(volMat, camera, visibility, elapsed, lightWorldPos, targetWorldPos, light.intensity);
      }

      // Particle glow state.
      light.getWorldPosition(glowState.source);
      glowState.direction.copy(targetWorldPos).sub(glowState.source).normalize();
      glowState.angle = DOCKING_LIGHT_ANGLE;
      glowState.range = DOCKING_LIGHT_DISTANCE;
      glowState.intensity = lightOn ? THREE.MathUtils.clamp(light.intensity * 0.08, 0, 1.2) : 0;
    }
  }

  setRenderTuning(tuning: RenderTuning): void {
    this.viewportView.setRenderTuning(tuning);
  }

  triggerCollisionCloud(strength: number, simulationX: number, simulationZ: number): void {
    this.viewportView.triggerCollisionCloud(strength, simulationX, simulationZ);
  }

  dispose(): void {
    this.viewportView.dispose();
  }
}

